import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { EmptyState, ErrorState } from '@/shared/ui';

import type { StoreSettingsDomain } from '../../model/permissions';
import { SETTINGS_NAV_BY_KEY } from '../settingsNav';

export interface ChannelPreviewPageProps {
  /** The sales channel (or any not-yet-built settings domain) to preview. */
  channel: StoreSettingsDomain;
}

/**
 * Preview page for a sales channel that is not yet available.
 *
 * Every channel except Telegram is a **preview of an upcoming capability** — the
 * integration (and its BFF endpoint) does not exist yet. This page is therefore
 * config-driven (header, icon and description come straight from the settings nav
 * model) and never calls a backend: it shows a permission-gated "coming soon"
 * state. When a channel ships, it graduates to its own real settings screen
 * (like Telegram or MAX). One component covers Web, Ozon, Wildberries, Yandex
 * Market and Lamoda.
 */
export function ChannelPreviewPage({ channel }: ChannelPreviewPageProps) {
  const { hasPermission } = usePermissions();
  const nav = SETTINGS_NAV_BY_KEY[channel];
  const canView = hasPermission(nav.viewPermission);

  return (
    <Box
      sx={{ width: '100%', mx: 'auto', maxWidth: 1100, px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}
    >
      <Button
        component={RouterLink}
        to="/settings"
        size="small"
        color="inherit"
        startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: 14 }} />}
        sx={{ mb: 2, ml: -1, color: 'text.secondary', display: { xs: 'inline-flex', md: 'none' } }}
      >
        All settings
      </Button>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 44,
            height: 44,
            borderRadius: 2.5,
            flexShrink: 0,
            color: '#fff',
            background: (t) =>
              `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`,
          }}
        >
          {nav.icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h4" component="h1">
              {nav.label}
            </Typography>
            <Chip size="small" variant="outlined" label="Preview" />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {nav.description}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mt: 3 }}>
        {canView ? (
          <EmptyState
            icon={<RocketLaunchRoundedIcon sx={{ fontSize: 48 }} />}
            title={`The ${nav.label} channel is coming soon`}
            description={`Selling on ${nav.label} is a preview of an upcoming capability. Right now only the Telegram channel is available; this channel's configuration will appear here once the integration is released.`}
          />
        ) : (
          <ErrorState
            title="Access restricted"
            message="You don't have permission to view these settings. Contact a tenant administrator if you need access."
          />
        )}
      </Box>
    </Box>
  );
}
